/**
 * The deck maths, with no DOM in it: where a moment falls in the beat, which
 * beats a window shows, what rate matches two tempos and how far to move a
 * track so its beats land on another's. Pure, so grid.test.ts can check it
 * with Node alone.
 *
 * The grid is FIXED -- one tempo and one offset per track, as the server fits
 * it -- so beat k is at offset + k * 60 / bpm seconds of the track's own time.
 * Everything here is in track time; the playback rate never enters, because
 * a beat is at the same place in the file however fast the file is played.
 */

export type Grid = { bpm: number; offset: number };

export const BEATS_PER_BAR = 4;

/** Seconds of track time per beat. */
export function beatPeriod(g: Grid): number {
  return 60 / g.bpm;
}

/** Beats since the grid's first beat, fractional and possibly negative. */
export function beatsSince(t: number, g: Grid): number {
  return (t - g.offset) / beatPeriod(g);
}

/** Where in its beat a moment falls, 0 on the beat and rising to 1. */
export function phaseOf(t: number, g: Grid): number {
  const b = beatsSince(t, g);
  return b - Math.floor(b);
}

const mod = (a: number, n: number) => ((a % n) + n) % n;

/** The beat a moment is in, and whether that beat is a bar line given which
 *  beat counts as the downbeat (barShift, 0-3). */
export function beatAt(t: number, g: Grid, barShift: number): { beat: number; bar: boolean } {
  const beat = Math.floor(beatsSince(t, g));
  return { beat, bar: mod(beat - barShift, BEATS_PER_BAR) === 0 };
}

/** Every beat between two moments of track time, in order. Nothing before
 *  the track starts: the grid extends backwards in principle and the file
 *  does not. */
export function gridLines(from: number, to: number, g: Grid, barShift: number): { t: number; bar: boolean }[] {
  const p = beatPeriod(g);
  const out: { t: number; bar: boolean }[] = [];
  for (let k = Math.max(0, Math.ceil((from - g.offset) / p)); ; k++) {
    const t = g.offset + k * p;
    if (t > to) break;
    out.push({ t, bar: mod(k - barShift, BEATS_PER_BAR) === 0 });
  }
  return out;
}

/**
 * The rate that plays the slave at the master's tempo, given the master's own
 * rate -- a master pitched up 4% is what the slave has to meet. Clamped to
 * the fader's range; `clamped` and `neededPct` let the page say the tempos
 * were too far apart rather than silently stopping short.
 */
export function syncRate(
  master: { bpm: number; rate: number },
  slave: { bpm: number },
  maxPct = 16,
): { rate: number; clamped: boolean; neededPct: number } {
  const want = (master.rate * master.bpm) / slave.bpm;
  const rate = Math.min(1 + maxPct / 100, Math.max(1 - maxPct / 100, want));
  return { rate, clamped: rate !== want, neededPct: (want - 1) * 100 };
}

/**
 * How far to move the slave, in its own seconds, so its phase equals the
 * master's: the shorter way round, so a slave three quarters of a beat late
 * is pushed a quarter forward rather than pulled three quarters back.
 */
export function phaseShift(masterPhase: number, slavePhase: number, slaveBeatSeconds: number): number {
  let d = masterPhase - slavePhase;
  d -= Math.round(d);
  return d * slaveBeatSeconds;
}

/** The waveform, back from the base64 the API sends it as. */
export function decodePeaks(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
